import { hide, show } from '@ebay/nice-modal-react'
import { StorageKeys, Translate, Translate2 } from 'common'
import { NonCachedSearchType } from 'interfaces'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { SaytRecommendation } from 'results'
import styled from 'styled-components'
import {
  CatalogIndexTracker,
  CatalogLookupType,
} from '../../../api/metrics/CatalogIndexTracker'
import { SaytOption } from '../../../api/searchResults/interfaces'
import { getInterchangeParts } from '../../../helpers/getInterchangeParts'
import SaytServiceProvider from '../../../services/SaytServiceProvider'
import { PartsCatalogSelections } from '../../../store/models/PartsCatalogModels'
import { PartsCatalogType } from '../../../store/partsCatalog/PartsCatalog'
import { useSearchStore } from '../../../store/search/SearchStore'
import { useSpecificConditionStore } from '../../../store/specificConditions/SpecificConditionsStore'
import { useUiStateStore } from '../../../store/uiState/UiStateStore'
import { useUserStore } from '../../../store/user/UserStore'
import { defaultTheme } from '../../../theme'
import { breakpoints } from '../../../theme/breakpoints'
import { ContactUsDrawer } from '../../navigation/header/contactUs/ContactUsDrawer'
import { useVehicleWidgetStore } from '../VehicleSearch/store/VehicleWidgetStore'
import AddVehicleForSearchModal from './components/AddVehicleForSearchModal'
import { MultiPartInquiryImportDrawer } from './components/MultiPartInquiryImportDrawer'
import { SearchField } from './components/SearchField'
import VehicleInfo from './components/VehicleInfo'
import { SpecificConditionsModal } from 'src/features/specificConditions/components/SpecificConditionsModal'

export const AllProductsSearch = observer((): ReactElement => {
  const searchStore = useSearchStore()
  const vehicleWidgetStore = useVehicleWidgetStore()
  const user = useUserStore()
  const navigate = useNavigate()
  const location = useLocation()
  const uiStore = useUiStateStore()
  const currentPathname = location.pathname

  const { lastNonCachedSearch, lastInterchangeTextSearched } = searchStore

  const {
    fetchSpecificConditionQuestions,
    areAllQuestionsAnswered,
    processQuestions,
  } = useSpecificConditionStore()

  const [interchangeTextFieldValue, setInterchangeTextFieldValue] = useState('')

  // MPV3-7765: prevent a user firing off the same search numerous times in a short timeframe.
  const [isSearchInProgress, setIsSearchInProgress] = useState<boolean>(false)

  const saveLocalStorageSearch = (search) => {
    if (search.trim() === '') {
      return
    }
    let searches: { id: string }[] = JSON.parse(
      localStorage.getItem(StorageKeys.PAST_SEARCHES)
    )
    if (searches) {
      // only add new terms.
      if (searches.findIndex((s) => s.id === search) === -1) {
        searches = [{ id: search }, ...searches]
      }
      searches = searches.slice(0, 10)
      localStorage.setItem(StorageKeys.PAST_SEARCHES, JSON.stringify(searches))
    } else {
      localStorage.setItem(
        StorageKeys.PAST_SEARCHES,
        JSON.stringify([{ id: search }])
      )
    }
  }

  const doSearchByText = async (
    text: string,
    catalogIndex: CatalogLookupType,
    saytSuggestions?: SaytOption[]
  ) => {
    searchStore.setShowPartsNotFitVehicleLabel(true)
    hide(AddVehicleForSearchModal)
    CatalogIndexTracker.setIndex(catalogIndex)
    await searchStore.searchByText(text)
    searchStore.handleAddBreadCrumb({
      page: Translate2('allProducts'),
      breadcumb: text,
      url: () => searchStore.searchByText(text),
    })
    storeSaytSuggestions(saytSuggestions)
  }

  const doVehicleSearch = async (
    text: string,
    catalogSelections: PartsCatalogSelections,
    saytSuggestions?: SaytOption[]
  ) => {
    searchStore.lastTextSearched = text
    searchStore.setShowPartsNotFitVehicleLabel(false)
    await handleShowFitmentConditions(catalogSelections, text)
    searchStore.handleAddBreadCrumb({
      page: Translate2('allProducts'),
      breadcumb: text,
      url: () => handleShowFitmentConditions(catalogSelections, text),
    })
    storeSaytSuggestions(saytSuggestions)
  }

  const skipAndSearch = async (
    text: string,
    type: CatalogLookupType,
    vehicleSearchCallback: () => void
  ) => {
    uiStore.setSkipAddVehicleCallback(vehicleSearchCallback)
    await doSearchByText(text, type)
    navigate('/searchResults')
  }

  const showAddVehicleModal = (
    text: string,
    type: CatalogLookupType,
    partsCatalogSelections: PartsCatalogSelections,
    saytSuggestions?: SaytOption[]
  ) => {
    const vehicleSearch = async () => {
      await doVehicleSearch(text, partsCatalogSelections)
      storeSaytSuggestions(saytSuggestions)
    }
    show(AddVehicleForSearchModal, {
      onAddVehicle: () => {
        vehicleWidgetStore.setAfterAddVehicle(vehicleSearch)
        vehicleWidgetStore.setIsDrawerOpened(true)
        hide(AddVehicleForSearchModal)
      },
      onSkipVehicle: async () => {
        await skipAndSearch(text, type, vehicleSearch)
        vehicleWidgetStore.setIsDrawerOpened(false)
        storeSaytSuggestions(saytSuggestions)
        hide(AddVehicleForSearchModal)
      },
    })
  }

  const onSelectedOption = (selected) => {
    // MPV3-7765: prevent a user firing off the same search numerous times in a short timeframe.
    if (
      [
        NonCachedSearchType.searchBySaytCategories,
        NonCachedSearchType.searchByText,
      ].includes(searchStore.lastNonCachedSearch) &&
      searchStore.lastTextSearched === selected.label &&
      searchStore.isLoading
    ) {
      return
    }
    const needVehicle = !searchStore.currentVehicle?.engine
    saveLocalStorageSearch(selected.label)
    if (selected.astSearch) {
      uiStore.setSkipAddVehicleCallback(undefined)
      doSearchByText(selected.label, CatalogLookupType.AST_MATCH)
      navigate('/searchResults')
    } else if (needVehicle) {
      showAddVehicleModal(
        selected.label,
        CatalogLookupType.AST_MATCH,
        selected.partCatalogMappings
      )
    } else {
      doVehicleSearch(selected.label, selected.partCatalogMappings)
    }
  }

  const storeSaytSuggestions = (suggestions: SaytOption[]): void => {
    if (!suggestions?.length) return
    const saytSuggestions: SaytRecommendation[] = suggestions.map((s) => {
      return {
        text: s.label,
        onClick: () => {
          onSearch(s.label?.trim())
        },
      }
    })
    searchStore.saytRecommendations = saytSuggestions
  }

  const onSearch = async (text, saytSuggestions?: SaytOption[]) => {
    // MPV3-7765: prevent a user firing off the same search numerous times in a short timeframe.
    if (
      ([
        NonCachedSearchType.searchBySaytCategories,
        NonCachedSearchType.searchByText,
      ].includes(searchStore.lastNonCachedSearch) &&
        searchStore.lastTextSearched === text &&
        searchStore.isLoading) ||
      isSearchInProgress
    ) {
      return
    }
    const needVehicle = !searchStore.currentVehicle?.engine
    saveLocalStorageSearch(text)
    const filteredSuggestions = saytSuggestions?.filter(
      (s) => s.label.toLowerCase() !== text.toLowerCase()
    )
    try {
      setIsSearchInProgress(true)
      const synonymResults = await SaytServiceProvider.getSaytSynonymMatches(
        text,
        user.getLanguage()
      )
      if (synonymResults) {
        if (needVehicle) {
          showAddVehicleModal(
            text,
            CatalogLookupType.AST_NO_MATCH,
            synonymResults.partCatalogMappings,
            filteredSuggestions
          )
          setIsSearchInProgress(false)
        } else {
          setIsSearchInProgress(false)
          await doVehicleSearch(
            text,
            synonymResults.partCatalogMappings,
            filteredSuggestions
          )
        }
      } else {
        uiStore.setSkipAddVehicleCallback(undefined)
        await doSearchByText(
          text,
          CatalogLookupType.AST_NO_MATCH,
          filteredSuggestions
        )
        setIsSearchInProgress(false)
        navigate('/searchResults')
      }
    } finally {
      setIsSearchInProgress(false)
    }
  }

  const handleShowFitmentConditions = async (
    catalogSelections: PartsCatalogSelections,
    searchText: string
  ) => {
    searchStore.setShowPartsNotFitVehicleLabel(false)

    const formattedVehicle = {
      ...searchStore.currentVehicle,
      initialized: undefined,
    }

    const terminologies = catalogSelections.reduce(
      (accumulator, currentValue) => {
        accumulator.push({
          groupId: +currentValue?.group?.id,
          id: +currentValue?.terminology?.id,
          value: '',
        })
        return accumulator
      },
      []
    )

    const specificConditionQuestions = await fetchSpecificConditionQuestions(
      formattedVehicle,
      terminologies,
      PartsCatalogType.PARTS_SEARCH
    )
    if (specificConditionQuestions.length > 0 && !areAllQuestionsAnswered()) {
      show(SpecificConditionsModal, {
        proceed: () => {
          searchStore.searchBySaytCategories(catalogSelections, 0, searchText)
          setIsSearchInProgress(false)
          navigate('/searchResults')
        },
      })
    } else {
      processQuestions()
      searchStore.handleAddBreadCrumb({
        page: Translate2('allProducts'),
        breadcumb: searchText,
        url: () =>
          searchStore.searchBySaytCategories(catalogSelections, 0, searchText),
      })
      searchStore.searchBySaytCategories(catalogSelections, 0, searchText)
      setIsSearchInProgress(false)
      navigate('/searchResults')
    }
  }

  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const onScroll = () => setOffset(window.pageYOffset)
    // clean up code
    window.removeEventListener('scroll', onScroll)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (
      lastNonCachedSearch === NonCachedSearchType.searchByInterchange &&
      (currentPathname === '/searchResults' ||
        currentPathname === '/cross-reference')
    ) {
      setInterchangeTextFieldValue(lastInterchangeTextSearched)
    } else {
      setInterchangeTextFieldValue('')
    }
  }, [lastInterchangeTextSearched, lastNonCachedSearch, currentPathname])

  // useEffect(() => {
  //   if (
  //     lastNonCachedSearch === NonCachedSearchType.searchByText &&
  //     currentPathname === '/searchResults'
  //   ) {
  //     setProductsSearch(lastTextSearched)
  //   } else {
  //     setProductsSearch('')
  //   }
  // }, [lastTextSearched, lastNonCachedSearch, currentPathname])

  useEffect(() => {
    if (currentPathname !== '/home' && currentPathname !== '/partsCatalog') {
      vehicleWidgetStore.setIsDrawerOpened(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPathname])

  return (
    <Wrapper show={offset} id="allProductsSearch">
      <FieldsWrapper>
        <VehicleInfoWrapper>
          <VehicleInfo
            vehicle={searchStore.currentVehicle}
            open={vehicleWidgetStore.isDrawerOpened}
            onClick={() =>
              vehicleWidgetStore.setIsDrawerOpened(
                !vehicleWidgetStore.isDrawerOpened
              )
            }
          />
        </VehicleInfoWrapper>
        <SearchBoxWrapper>
          <SearchField
            label={Translate('products')}
            placeholder={Translate('searchPartBrandName')}
            onSearch={(text, saytSuggestions) => {
              if (text.length > 0) {
                onSearch(text.trim(), saytSuggestions)
              }
            }}
            onSelectOption={onSelectedOption}
            type="sayt"
            multiPartInquiry={
              import.meta.env.VITE_FEATURE_MULTI_PART_INQUIRY_ENABLED === 'true'
            }
          />
        </SearchBoxWrapper>

        <OrDivider>{Translate('or')}</OrDivider>

        <SearchBoxWrapper>
          <SearchField
            label={Translate('interchange')}
            placeholder={Translate('separateMultiplePartNumbers')}
            onSearch={(text) => {
              getInterchangeParts(text)
            }}
            type="interchange"
            textFieldValue={interchangeTextFieldValue}
          />
        </SearchBoxWrapper>
      </FieldsWrapper>
      <ContactUsDrawer
        open={uiStore.showContactDrawer}
        onCloseDrawer={() => uiStore.setShowContactDrawer(false)}
      />
      <MultiPartInquiryImportDrawer />
    </Wrapper>
  )
})

interface WrapperProps {
  show: number
}
const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  min-height: 100px;
  height: fit-content;
  box-shadow: 0 6px 6px -6px rgba(0, 0, 0, 0.3);
  background-color: #fff;
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  @media print {
    display: none;
  }
`

const VehicleInfoWrapper = styled.div`
  flex: 0 1 auto;
  margin: 0px 32px 0px 0px;
  background-color: ${defaultTheme.colors.rowFeaturedBackground};
  height: 56px;
  width: 100%;
  overflow: hidden;

  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: 728px;
  }

  @media screen and (${breakpoints.laptopLarge}px < width <= ${breakpoints.laptopXLarge}px) {
    min-width: 527px;
    max-width: 728px;
    margin: 0px 32px 0px 0px;
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopLarge}px) {
    min-width: 385px;
    max-width: 527px;
    margin: 0px 16px 0px 0px;
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    min-width: 289px;
    max-width: 385px;
    margin: 0px 20px 0px 0px;
  }

  @media screen and (width <= ${breakpoints.laptop}px) {
    min-width: 288px;
    max-width: 289px;
    margin: 0px 17px 0px 0px;
  }

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    display: none;
  }
`

const FieldsWrapper = styled.div`
  padding: 22px 20px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: inherit;
  width: 100%;
  min-width: 0;
  @media screen and (width < ${breakpoints.tabletLarge}px) {
    flex-wrap: wrap;
    padding: 12px 16px;
  }
`
const OrDivider = styled.span`
  font-family: Oswald, serif;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 30px;
  margin-left: 10px;
  margin-right: 10px;

  @media (width > ${breakpoints.laptopXLarge}px) {
    margin-left: 24px;
    margin-right: 24px;
  }

  @media (width <= ${breakpoints.laptopXLarge}px) {
    margin-left: 16px;
    margin-right: 16px;
  }

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
    &::before,
    &::after {
      content: '';
      flex: 1;
      border-top: 1px solid ${defaultTheme.colors.border};
    }
  }
`

const SearchBoxWrapper = styled.div`
  min-width: 0;
  width: 100%;
  @media screen and (width > ${breakpoints.laptopXLarge}px) {
    width: 519px;
  }

  @media screen and (${breakpoints.laptopLarge}px < width <= ${breakpoints.laptopXLarge}px) {
    min-width: 395px;
    max-width: 519px;
  }

  @media screen and (${breakpoints.laptopMedium}px < width <= ${breakpoints.laptopLarge}px) {
    min-width: 395px;
    max-width: 395px;
  }

  @media screen and (${breakpoints.laptop}px < width <= ${breakpoints.laptopMedium}px) {
    min-width: 310px;
    max-width: 395px;
  }

  @media screen and (${breakpoints.tabletLarge}px <= width <= ${breakpoints.laptop}px) {
    min-width: 303px;
    max-width: 310px;
  }

  @media screen and (width < ${breakpoints.tabletLarge}px) {
    flex: 1 1 100%;
    width: 100%;
    height: 36px;
  }
`
